// Content registry: every SRD JSON under `src/data/srd/` is auto-imported via
// Webpack's `require.context`. New content files appear in the registry on
// next save with no edits to this file.
//
// Each content type has its own `require.context` so adjacent siblings (like
// `subclasses/cleric/life-domain.json` vs `subclasses/fighter/champion.json`)
// resolve cleanly. The `byId` aggregator dedupes by the file's `id` field.
//
// Lookups return `null` for unknown ids so consumers can degrade gracefully
// while Phase 3 fills in remaining content.

/* eslint-disable import/no-webpack-loader-syntax */

const loadFromContext = (ctx) => {
  const result = {};
  for (const key of ctx.keys()) {
    const mod = ctx(key);
    const data = mod && mod.default ? mod.default : mod;
    if (data && data.id) result[data.id] = data;
  }
  return result;
};

// `require.context` is webpack-only. Under Jest (Node), it doesn't exist —
// the engine's lookups all return `null` in that environment, which the
// engine handles gracefully. Component tests that don't mock contentRegistry
// will see "missing content" pills but no crashes; engine tests should
// `jest.mock('./contentRegistry', ...)` to supply fixtures.
const safeContext = (dir, recursive, pattern) => {
  if (typeof require !== 'undefined' && typeof require.context === 'function') {
    return loadFromContext(require.context(dir, recursive, pattern));
  }
  return {};
};

const CLASSES = safeContext('../../../data/srd/classes', false, /\.json$/);
const SUBCLASSES = safeContext('../../../data/srd/subclasses', true, /\.json$/);
const SPECIES = safeContext('../../../data/srd/species', false, /\.json$/);
const BACKGROUNDS = safeContext('../../../data/srd/backgrounds', false, /\.json$/);
const STARTER_KITS = safeContext('../../../data/srd/starter-kits/by-id', false, /\.json$/);

// Items live in five sub-trees; merge them into one map (they all share
// the same `srd.weapon.*` / `srd.armor.*` / `srd.shield.*` / `srd.tool.*` /
// `srd.gear.*` namespace).
const WEAPONS = safeContext('../../../data/srd/items/weapons/by-id', false, /\.json$/);
const ARMOR = safeContext('../../../data/srd/items/armor/by-id', false, /\.json$/);
const TOOLS = safeContext('../../../data/srd/items/tools/by-id', false, /\.json$/);
const GEAR = safeContext('../../../data/srd/items/adventuring-gear/by-id', false, /\.json$/);
const ITEMS = { ...WEAPONS, ...ARMOR, ...TOOLS, ...GEAR };

const SPELLS = safeContext('../../../data/srd/spells/by-id', false, /\.json$/);

/* eslint-enable import/no-webpack-loader-syntax */

export const getClass = (id) => CLASSES[id] || null;
export const getSubclass = (id) => SUBCLASSES[id] || null;
export const getSpecies = (id) => SPECIES[id] || null;
export const getBackground = (id) => BACKGROUNDS[id] || null;
export const getStarterKit = (id) => STARTER_KITS[id] || null;
export const getItem = (id) => ITEMS[id] || null;
export const getSpell = (id) => SPELLS[id] || null;

export const listClasses = () => Object.values(CLASSES);
export const listSpecies = () => Object.values(SPECIES);
export const listBackgrounds = () => Object.values(BACKGROUNDS);
export const listSpells = () => Object.values(SPELLS);

// Quick stats for debugging / dev tools.
export const registryStats = () => ({
  classes: Object.keys(CLASSES).length,
  subclasses: Object.keys(SUBCLASSES).length,
  species: Object.keys(SPECIES).length,
  backgrounds: Object.keys(BACKGROUNDS).length,
  starterKits: Object.keys(STARTER_KITS).length,
  items: Object.keys(ITEMS).length,
  spells: Object.keys(SPELLS).length,
});
